import { useState } from 'react'

import { getEditedLabel, getEventWhen } from '../../../../StayTimeline.utils'
import { ChannelTag } from '../../../ChannelTag'
import { NoteActions } from '../../../NoteActions'
import { NoteEditor } from '../../../NoteEditor'
import { Pill } from '../../../Shared.styles'
import { TimelineAvatar } from '../../../TimelineAvatar'
import { MessageBubble } from '../MessageBubble'
import * as Bubble from '../MessageBubble/MessageBubble.styles'
import { MessageMarkdown } from '../MessageMarkdown'
import * as S from './InternalNote.styles'
import { InternalNoteProps, NoteStatus } from './InternalNote.types'

export const InternalNote = ({ event, flags, checkIn, checkout, actions }: InternalNoteProps) => {
  const [status, setStatus] = useState<NoteStatus>('idle')

  const closeEditor = () => {
    setStatus('idle')
  }

  const save = async (body: string) => {
    setStatus('saving')

    const success = await actions?.onUpdate(body)

    if (success) {
      closeEditor()
    } else {
      setStatus('editing')
    }

    return !!success
  }

  const remove = async () => {
    setStatus('deleting')

    const success = (await actions?.onDelete()) ?? false
    if (!success) setStatus('idle')
  }

  const name = event.who || 'AvantStay'
  const canChange = !!event.noteId && !!actions
  const editedLabel = getEditedLabel(event)
  const isEditorOpen = status === 'editing' || status === 'saving'
  const isDeleting = status === 'deleting'

  return (
    <MessageBubble
      isInbound={false}
      flags={flags}
      avatar={<TimelineAvatar name={name} variant="teammate" />}
      header={
        <>
          <Pill $tone="warn">Internal note</Pill>
          <ChannelTag channel={event.channel} medium={event.medium} />
          <Bubble.Name>{name}</Bubble.Name>
        </>
      }
    >
      <S.Body>
        {isEditorOpen ? (
          <NoteEditor
            initialBody={event.body ?? ''}
            isSaving={status === 'saving'}
            onCancel={closeEditor}
            onSave={save}
          />
        ) : (
          <S.BubbleLine>
            {canChange ? (
              <S.ActionsSlot>
                <NoteActions
                  isDisabled={isDeleting}
                  onEdit={() => setStatus('editing')}
                  onDelete={() => void remove()}
                />
              </S.ActionsSlot>
            ) : null}

            <Bubble.Bubble
              as="div"
              $variant="note"
              $isRunStart={flags.isFirst}
              $isPending={isDeleting}
            >
              <MessageMarkdown body={event.body} />
            </Bubble.Bubble>
          </S.BubbleLine>
        )}
      </S.Body>

      {flags.isLast || editedLabel ? (
        <Bubble.When>
          {getEventWhen(event.at, checkIn, checkout)}
          {editedLabel ? <span title={editedLabel}> (Edited)</span> : null}
        </Bubble.When>
      ) : null}
    </MessageBubble>
  )
}
